import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../App';
import AdminHeader from '../../components/AdminHeader';
import { 
  Search, 
  RefreshCw,
  Calendar,
  User,
  ChevronRight,
  Clock,
  AlertCircle
} from 'lucide-react';

// Event type colors
const EVENT_COLORS = {
  GAME_CREATED: 'bg-green-100 text-green-700',
  GAME_CANCELED: 'bg-red-100 text-red-700',
  INVITE_SENT: 'bg-blue-100 text-blue-700',
  INVITE_ACCEPTED: 'bg-green-100 text-green-700',
  USER_JOINED: 'bg-green-100 text-green-700',
  USER_LEFT: 'bg-yellow-100 text-yellow-700',
  WAITLIST_PROMOTED: 'bg-purple-100 text-purple-700',
  PLAYER_REMOVED: 'bg-red-100 text-red-700',
  CONFIRMATION_REQUEST_SENT: 'bg-blue-100 text-blue-700',
  USER_CONFIRMED: 'bg-green-100 text-green-700',
  NOTIFICATION_CREATED: 'bg-gray-100 text-gray-700',
  NOTIFICATION_SENT: 'bg-green-100 text-green-700',
  NOTIFICATION_SUPPRESSED: 'bg-orange-100 text-orange-700',
};

import { parseUTC } from '../../utils/datetime';

// Format time in user's local timezone with abbreviation
const formatLocalTime = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = parseUTC(timestamp);
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
};

// Build ISO string for start of a local day from YYYY-MM-DD input
const localDayStart = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
};

// Build ISO string for end of a local day from YYYY-MM-DD input
const localDayEnd = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};

// Format date for input fields (YYYY-MM-DD) in local timezone
const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Invalid URL values keep the existing initial default, not Date's rollover.
const validURLDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

export default function GameTapeSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [gameId, setGameId] = useState(searchParams.get('game_id') || '');
  const [userId, setUserId] = useState(searchParams.get('user_id') || '');
  const [eventType, setEventType] = useState(searchParams.get('event_type') || '');
  const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');
  
  // Fetch event types on mount
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const response = await apiClient.get('/admin/game-tape/event-types');
        setEventTypes(response.data.event_types || []);
      } catch (err) {
        console.error('Failed to fetch event types:', err);
      }
    };
    fetchEventTypes();
  }, []);
  
  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      // Only add non-empty filters
      if (gameId && gameId.trim()) {
        params.append('game_id', gameId.trim());
      }
      if (userId && userId.trim()) {
        params.append('user_id', userId.trim());
      }
      if (eventType) {
        params.append('event_type', eventType);
      }
      if (startDate) {
        params.append('start_date', localDayStart(startDate));
      }
      if (endDate) {
        params.append('end_date', localDayEnd(endDate));
      }
      params.append('limit', '100');
      
      const response = await apiClient.get(`/admin/game-tape?${params.toString()}`);
      
      setEvents(response.data.events || []);
      setTotal(response.data.total || 0);
      
      // Update URL params for bookmarking
      const urlParams = new URLSearchParams();
      if (gameId) urlParams.set('game_id', gameId);
      if (userId) urlParams.set('user_id', userId);
      if (eventType) urlParams.set('event_type', eventType);
      if (startDate) urlParams.set('start_date', startDate);
      if (endDate) urlParams.set('end_date', endDate);
      setSearchParams(urlParams);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchEvents();
  };
  
  const handleClearFilters = () => {
    setGameId('');
    setUserId('');
    setEventType('');
    setStartDate('');
    setEndDate('');
    setEvents([]);
    setTotal(0);
    setSearchParams(new URLSearchParams());
  };
  
  const viewGameTimeline = (gId) => {
    navigate(`/admin/games/${gId}/timeline`);
  };
  
  // Set default date range (last 7 days) on first load and auto-fetch
  useEffect(() => {
    if (!searchParams.get('game_id') && !searchParams.get('user_id')) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      const suppliedStart = searchParams.get('start_date');
      const suppliedEnd = searchParams.get('end_date');
      const sd = validURLDate(suppliedStart) ? suppliedStart : formatDateForInput(start);
      const ed = validURLDate(suppliedEnd) ? suppliedEnd : formatDateForInput(end);
      setStartDate(sd);
      setEndDate(ed);
      
      // Auto-fetch with default date range
      const autoFetch = async () => {
        setLoading(true);
        try {
          const params = new URLSearchParams();
          params.append('start_date', localDayStart(sd));
          params.append('end_date', localDayEnd(ed));
          params.append('limit', '100');
          const response = await apiClient.get(`/admin/game-tape?${params.toString()}`);
          setEvents(response.data.events || []);
          setTotal(response.data.total || 0);
        } catch (err) {
          setError(err.response?.data?.detail || err.message || 'Failed to fetch events');
        } finally {
          setLoading(false);
        }
      };
      autoFetch();
    }
  // eslint-disable-next-line
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6" data-testid="game-tape-search">
      <div className="max-w-6xl mx-auto">
        {/* Admin Header */}
        <AdminHeader title="GAME TAPE" />
        
        {/* Page Description */}
        <p className="text-gray-500 mb-6">Query immutable domain events across all games</p>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Game ID</label>
              <input
                type="text"
                value={gameId}
                onChange={(e) => setGameId(e.target.value)}
                placeholder="Enter game ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                data-testid="game-id-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Username or user ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                data-testid="user-id-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                data-testid="event-type-select"
              >
                <option value="">All Types</option>
                {eventTypes.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                data-testid="start-date-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                data-testid="end-date-input"
              />
            </div>
            
            <div className="flex items-end gap-2">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 text-sm"
                disabled={loading}
                data-testid="search-button"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Search
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
              >
                Clear
              </button>
            </div>
          </div>
        </form>
        
        {/* Results */}
        {error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        ) : events.length > 0 ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-500 mb-2">
              Showing {events.length} of {total} events
            </div>
            
            {events.map((event, index) => (
              <div
                key={event.id || index}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                data-testid={`event-row-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${EVENT_COLORS[event.event_type] || 'bg-gray-100 text-gray-700'}`}>
                        {event.event_type.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatLocalTime(event.created_at)}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      {event.game_title && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3 text-gray-400" />
                          <span className="font-medium">{event.game_title}</span>
                          <span className="text-xs text-gray-400">({event.game_id?.substring(0, 8)}...)</span>
                        </div>
                      )}
                      
                      {event.actor_username && (
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-gray-400" />
                          <span>Actor: {event.actor_username}</span>
                        </div>
                      )}
                      
                      {event.target_username && event.target_user_id !== event.actor_user_id && (
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-gray-400" />
                          <span>Target: {event.target_username}</span>
                        </div>
                      )}
                      
                      {event.roster_count_snapshot !== null && event.roster_count_snapshot !== undefined && (
                        <div className="text-xs text-gray-400">
                          Roster: {event.roster_count_snapshot} players
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => viewGameTimeline(event.game_id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="View game timeline"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : !loading && (gameId || userId || eventType || startDate || endDate) ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            No events found matching your criteria.
          </div>
        ) : !loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            Enter search criteria and click Search to query events.
          </div>
        ) : null}
      </div>
    </div>
  );
}
